import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import UseAxios from '../../../Api/http/http';
import { uploadBillingReport } from '../../../Api/request/billingReport';
import { getProjectFileUploadSampleFile } from '../../../Api/request/projectFileUpload';

const TM_BILLING_EFFORTS_SAMPLE_FILE_TYPE = 'tm_billing_efforts';
const AMOUNT_RESTRICTED_SAMPLE_URL = 'https://storage.googleapis.com/'
  + 'darpan2-non-prod-deal-trasition/sample-files/'
  + 'sample_file_tm_billing_approval_automation_efforts.csv';

const BILLING_HEADERS = {
  employeeId: 'employee id',
  employeeName: 'employee name',
  billedEffort: 'billed effort',
  totalAmountInr: 'total amount (inr)',
};

const DEFAULT_HEADER_COLUMNS = {
  employeeId: 1,
  employeeName: 2,
  billedEffort: 4,
  totalAmountInr: 5,
};

const normalizeHeader = (value) => `${value ?? ''}`.trim().toLowerCase();

const cellValue = (value) => {
  if (value && typeof value === 'object') {
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text).join('');
    if (value.text) return value.text;
    if (value.result !== undefined) return value.result;
  }
  return value;
};

const getCellError = (rowNumber, columnNumber, columnName, issue) => (
  new Error(`Row ${rowNumber}, Column ${columnNumber} (${columnName}): ${issue}`)
);

const parseDecimalCell = (value, rowNumber, columnNumber, columnName) => {
  const rawValue = cellValue(value);
  const text = `${rawValue ?? ''}`.trim();
  if (!text) {
    throw getCellError(rowNumber, columnNumber, columnName, 'A value is required.');
  }
  if ((typeof rawValue !== 'number' && !/^\d+(?:\.\d+)?$/.test(text))
    || !Number.isFinite(Number(text))) {
    throw getCellError(
      rowNumber,
      columnNumber,
      columnName,
      'Enter a non-negative decimal number without commas or other characters.',
    );
  }
  return Number(text);
};

const parseRows = (matrix, canAccessAmount) => {
  const headerIndex = matrix.findIndex((row) => {
    const headers = row.map(normalizeHeader);
    return ['employee id', 'employee name', 'billed effort']
      .every((header) => headers.includes(header));
  });

  if (headerIndex < 0) {
    throw getCellError(1, 1, 'Headers', 'Could not find the billing report header row.');
  }

  const headers = matrix[headerIndex].map(normalizeHeader);
  const indexes = Object.fromEntries(Object.entries(BILLING_HEADERS).map(([field, header]) => (
    [field, headers.indexOf(header)]
  )));
  if (indexes.totalAmountInr >= 0 && !canAccessAmount) {
    throw new Error('You are not allowed to add Total Amount (INR).');
  }
  if (canAccessAmount && indexes.totalAmountInr < 0) {
    throw getCellError(
      headerIndex + 1,
      DEFAULT_HEADER_COLUMNS.totalAmountInr,
      BILLING_HEADERS.totalAmountInr,
      'Required column header is missing.',
    );
  }
  Object.entries(indexes).filter(([field]) => field !== 'totalAmountInr' || canAccessAmount)
    .forEach(([field, index]) => {
      if (index < 0) {
        throw getCellError(
          headerIndex + 1,
          DEFAULT_HEADER_COLUMNS[field],
          BILLING_HEADERS[field],
          'Required column header is missing.',
        );
      }
    });

  const rows = [];
  const seenEmployees = new Set();
  matrix.slice(headerIndex + 1).forEach((sourceRow, rowOffset) => {
    const row = sourceRow || [];
    const rowNumber = headerIndex + rowOffset + 2;
    const cellText = (field) => `${cellValue(row[indexes[field]]) ?? ''}`.trim();
    const relevantIndexes = Object.entries(indexes)
      .filter(([field]) => field !== 'totalAmountInr' || canAccessAmount)
      .map(([, index]) => index);
    if (relevantIndexes.every((index) => !`${cellValue(row[index]) ?? ''}`.trim())) return;
    if (cellText('employeeId').toLowerCase() === 'total'
      || cellText('employeeName').toLowerCase() === 'total') return;
    const amountCellPresent = row
      .slice(DEFAULT_HEADER_COLUMNS.totalAmountInr - 1)
      .some((value) => `${cellValue(value) ?? ''}`.trim());
    if (!canAccessAmount && amountCellPresent) {
      throw new Error('You are not allowed to add Total Amount (INR).');
    }

    const employeeId = cellText('employeeId');
    const employeeName = cellText('employeeName');
    if (!employeeId) {
      throw getCellError(rowNumber, indexes.employeeId + 1, 'Employee ID', 'A value is required.');
    }
    if (!employeeName) {
      throw getCellError(rowNumber, indexes.employeeName + 1, 'Employee Name', 'A value is required.');
    }
    const employeeKey = employeeId.toUpperCase();
    if (seenEmployees.has(employeeKey)) {
      throw getCellError(
        rowNumber,
        indexes.employeeId + 1,
        'Employee ID',
        `Employee ${employeeId} appears more than once in the file.`,
      );
    }
    seenEmployees.add(employeeKey);
    rows.push({
      employeeId,
      employeeName,
      billedEffort: parseDecimalCell(
        row[indexes.billedEffort], rowNumber, indexes.billedEffort + 1, 'Billed Effort',
      ),
      ...(canAccessAmount ? {
        totalAmountInr: parseDecimalCell(
          row[indexes.totalAmountInr], rowNumber, indexes.totalAmountInr + 1, 'Total Amount (INR)',
        ),
      } : {}),
      rowNumber,
      columnNumbers: Object.fromEntries(Object.entries(indexes)
        .filter(([field]) => field !== 'totalAmountInr' || canAccessAmount)
        .map(([field, index]) => [field, index + 1])),
    });
  });
  if (!rows.length) {
    throw getCellError(headerIndex + 2, indexes.employeeId + 1, 'Employee ID', 'No billing rows were found.');
  }
  return rows;
};

export const parseBillingReportFile = async (file, canAccessAmount) => {
  if (file.name.toLowerCase().endsWith('.csv')) {
    const Papa = (await import('papaparse')).default;
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        skipEmptyLines: false,
        complete: (result) => {
          if (result.errors?.length) {
            const firstError = result.errors[0];
            reject(getCellError(
              Number(firstError.row || 0) + 1,
              1,
              'CSV row',
              firstError.message || 'Unable to read this row.',
            ));
            return;
          }
          try {
            resolve(parseRows(result.data, canAccessAmount));
          } catch (error) {
            reject(error);
          }
        },
      });
    });
  }

  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets[0];
  if (!worksheet) throw new Error('The Excel file has no worksheet.');
  const matrix = [];
  worksheet.eachRow({ includeEmpty: true }, (row) => {
    matrix.push(row.values.slice(1).map(cellValue));
  });
  return parseRows(matrix, canAccessAmount);
};

const downloadAmountRestrictedSample = () => {
  const link = document.createElement('a');
  link.href = AMOUNT_RESTRICTED_SAMPLE_URL;
  link.download = 'sample_file_tm_billing_approval_automation_efforts.csv';
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
};

export const downloadBillingReportSample = async (canAccessAmount) => {
  if (!canAccessAmount) {
    downloadAmountRestrictedSample();
    return;
  }
  const response = await UseAxios(getProjectFileUploadSampleFile(TM_BILLING_EFFORTS_SAMPLE_FILE_TYPE));

  if (response?.data?.isAxiosError || response?.data instanceof Error) {
    const axiosError = response.data;
    throw new Error(
      axiosError?.response?.data?.message
      || axiosError?.response?.data?.error
      || axiosError?.message
      || 'Failed to download sample file.',
    );
  }

  const signedUrl = response?.data?.data?.signedUrl;

  if (!signedUrl) {
    throw new Error('Download URL not found.');
  }

  const link = document.createElement('a');
  link.href = signedUrl;
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
};

const useBillingReportUpload = ({
  appliedProjectAndDate,
  canAccessAmount,
  refreshTable,
  userInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const onFileChange = useCallback((event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = '';
    if (!selectedFile) return;
    if (!/\.(xlsx|csv)$/i.test(selectedFile.name)) {
      toast.error('Please choose an Excel (.xlsx) or CSV file.');
      setFile(null);
      return;
    }
    setFile(selectedFile);
  }, []);

  const onUpload = useCallback(async () => {
    if (!file) {
      toast.error('Please choose a file to upload.');
      return;
    }
    setIsUploading(true);
    try {
      const rows = await parseBillingReportFile(file, canAccessAmount);
      const response = await uploadBillingReport({
        projectId: appliedProjectAndDate.projectId,
        periodStartDate: appliedProjectAndDate.periodStartDate,
        periodEndDate: appliedProjectAndDate.periodEndDate,
        rows,
        userInfo,
      });
      if (!response?.data?.success) {
        const error = response?.data;
        throw new Error(
          error?.response?.data?.message
          || error?.message
          || 'Unable to upload billing report.',
        );
      }
      setIsOpen(false);
      setFile(null);
      toast.success(response.data.message || 'Billing report uploaded successfully.');
      try {
        await refreshTable();
      } catch {
        toast.error('Upload succeeded, but the report could not be refreshed.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to upload billing report.');
    } finally {
      setIsUploading(false);
    }
  }, [appliedProjectAndDate, canAccessAmount, file, refreshTable, userInfo]);

  const open = useCallback(() => {
    setFile(null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isUploading) return;
    setIsOpen(false);
    setFile(null);
  }, [isUploading]);

  const downloadSample = useCallback(async () => {
    try {
      await downloadBillingReportSample(canAccessAmount);
    } catch (error) {
      toast.error(error?.message || 'Unable to download the sample file.');
    }
  }, [canAccessAmount]);

  return {
    close,
    downloadSample,
    fileName: file?.name || '',
    isOpen,
    isUploading,
    onFileChange,
    onUpload,
    open,
  };
};

export default useBillingReportUpload;
